import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ChevronDown, Edit2, Layers, Save, Search, X } from "react-feather";

import { AppState } from "../../../redux/types";
import { cn } from "../../../utils/commonFunctions";
import { setPutApiStatus } from "../../../redux/actions/apiStatusActions";
import {
  editValueAddedServices,
  getValueAddedServiceList,
} from "../../../redux/actions/master/valueAddedServiceActions";
import {
  IValueAddedService,
  IValueAddedServiceUpdatePayload,
} from "../../../types/master/valueAddedServiceInterface";
import { Breadcrumbs, CardHeader, Dash, ghostBtn, primaryBtn, tdClass, thClass } from "../billing/billingUi";

// Name, description, applied on and calculation type are fixed here; the API only stores value / minimum per code

export type CalculationType = "Fixed" | "Percentage" | "KGS" | "Per ₹1 Diesel Increase" | "";

export interface IVasCharge {
  id: number;
  code: string;
  // id of the matching value_added_services_master row, filled from the API
  dbId: number | null;
  name: string;
  description: string;
  appliedOn: string;
  calculationType: CalculationType;
  value: string;
  minimum: string;
}

export interface IVasCategory {
  key: string;
  title: string;
  charges: IVasCharge[];
}

const charge = (
  id: number,
  code: string,
  name: string,
  description: string,
  appliedOn = "",
  calculationType: CalculationType = ""
): IVasCharge => ({ id, code, dbId: null, name, description, appliedOn, calculationType, value: "", minimum: "" });

export const DEFAULT_CATEGORIES: IVasCategory[] = [
  {
    key: "basic",
    title: "Basic Freight",
    charges: [
      charge(1, "BASIC_DOCKET_CHARGE", "Basic Docket Charge", "Basic Docket Charge", "Per LR", "Fixed"),
      charge(2, "MINIMUM_FREIGHT", "Minimum Freight", "Minimum Freight per Shipment", "Per LR", "Fixed"),
      charge(3, "MINIMUM_CHARGEABLE_WEIGHT", "Minimum Chargeable Weight", "Minimum Billable Weight", "Per LR", "Fixed"),
      charge(4, "CFT_CONVERSION_FACTOR", "CFT Conversion Factor", "Cubic Feet Conversion Factor", "Volumetric", "Fixed"),
    ],
  },
  {
    key: "fuel",
    title: "Fuel & Risk Charges",
    charges: [
      charge(6, "FUEL_SURCHARGE", "Fuel Surcharge (FSC)", "Applicable on Base Freight", "Base Freight", "Percentage"),
      charge(7, "FUEL_ESCALATION", "Fuel Escalation", "Diesel Price Increase", "FSC", "Per ₹1 Diesel Increase"),
    ],
  },
  {
    key: "insurance",
    title: "Insurance",
    charges: [
      charge(8, "OWNER_RISK", "Owner Risk (FOV/ROV)", "Invoice Value Based", "INV Value", "Percentage"),
      charge(9, "CARRIER_RISK", "Carrier Risk", "As per Agreement (if not provided leave blank)", "INV Value", "Percentage"),
    ],
  },
  {
    key: "delivery",
    title: "Delivery Related Value Added Services (VAS)",
    charges: [
      charge(10, "ODA_LEVEL_1", "ODA Level 1", "Out of Delivery Area", "Chargeable Weight", "KGS"),
      charge(11, "ODA_LEVEL_2", "ODA Level 2", "Remote Delivery Area", "Chargeable Weight", "KGS"),
      charge(12, "ODA_LEVEL_3", "ODA Level 3", "Highly Remote Area", "Chargeable Weight", "KGS"),
      charge(13, "TO_PAY_CHARGES", "To-Pay Charges", "Freight Collection at Delivery", "Per LR", "Fixed"),
      charge(14, "SUNDAY_HOLIDAY_DELIVERY", "Sunday / Holiday Delivery", "Special Delivery", "Per LR", "Fixed"),
      charge(15, "REATTEMPT_DELIVERY", "Re-attempt Delivery", "Charge after 2 Failed Attempts", "Chargeable Weight", "KGS"),
      charge(16, "CASH_ON_DELIVERY", "Cash on Delivery (COD)", "Collection Charge", "Invoice Value", "Percentage"),
      charge(17, "HARD_COPY_POD", "Hard Copy POD", "Physical POD Delivery", "Per LR", "Fixed"),
      charge(18, "WAREHOUSE_DEMURRAGE", "Warehouse / Demurrage", "Storage beyond Free Period (First 6 Days Free)", "Per Kg/Day", "KGS"),
      charge(19, "SPECIAL_DELIVERY", "Special Delivery", "Timed / Mall / Govt / CSD Delivery", "Chargeable Weight", "KGS"),
      charge(20, "FLOOR_DELIVERY", "Floor Delivery", "1st Floor Onwards", "Chargeable Weight", "KGS"),
    ],
  },
  {
    key: "handling",
    title: "Special Handling Charges",
    charges: [
      charge(5, "HEAVY_SHIPMENT_HANDLING", "Handling Charges (Heavy Shipment)", "Single Package Weight > 50 Kg", "Per KG", "KGS"),
      charge(21, "CRANE_PULLEY_EQUIPMENT", "Crane / Pulley / Equipment", "Loading or Unloading Equipment", "As per Actual"),
      charge(22, "WARAI_UNION_MATHADI", "Warai / Union / Mathadi Charges", "Labour Charges", "As per Actual"),
      charge(23, "LIQUID_HANDLING", "Liquid Handling Charges", "Liquid Shipments", "Chargeable Weight", "KGS"),
      charge(24, "GREEN_TAX_DELHI", "Green Tax (Delhi)", "Environmental Charge", "Chargeable Weight", "KGS"),
      charge(25, "JK_KERALA_NE_CHARGES", "J&K / Kerala / North-East Charges", "Remote Region Handling", "Chargeable Weight", "KGS"),
      charge(26, "VALUABLE_CARGO_HANDLING", "Valuable Cargo Handling", "Shipment Value > ₹15,000 per Kg", "As per Approval"),
    ],
  },
  {
    key: "misc",
    title: "Miscellaneous Charges",
    charges: [
      charge(28, "TOTAL_WEIGHT_ROUNDING", "Total Weight Rounding", "Rounded to Next Full Kilogram"),
      charge(29, "FREIGHT_ROUNDING", "Freight Rounding", "Rounded to Next Full Rupee"),
      charge(30, "UNFORESEEN_EXPENSES", "Unforeseen Expenses", "Charged on Actual Basis with Approval"),
    ],
  },
];

const cloneCategories = (source: IVasCategory[] = DEFAULT_CATEGORIES) =>
  source.map((category) => ({
    ...category,
    charges: category.charges.map((item) => ({ ...item })),
  }));

// DECIMAL(12,4) comes back as "100.0000"; trim it to "100" for display and editing
export const toInputValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : "";
};

const toPayloadValue = (value: string) => (value.trim() === "" ? null : Number(value));

export const isInvalidNumber = (value: string) => value.trim() !== "" && !Number.isFinite(Number(value));

const mergeApiValues = (records: IValueAddedService[]) => {
  const byCode = new Map(records.map((record) => [record.code, record]));
  return DEFAULT_CATEGORIES.map((category) => ({
    ...category,
    charges: category.charges.map((item) => {
      const record = byCode.get(item.code);
      return {
        ...item,
        dbId: record?.id ?? null,
        value: toInputValue(record?.value),
        minimum: toInputValue(record?.minimum),
      };
    }),
  }));
};

export const inputClass =
  "h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[13px] text-slate-700 transition placeholder:text-slate-300 focus:border-[#0159DC] focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400";

export const getValueAffix = (type: CalculationType) => {
  if (type === "Percentage") return { suffix: "%" };
  if (type === "KGS") return { prefix: "₹", suffix: "/kg" };
  if (type === "Fixed") return { prefix: "₹" };
  if (type === "Per ₹1 Diesel Increase") return { prefix: "+", suffix: "%" };
  return {};
};

export const formatValue = (value: string, prefix?: string, suffix?: string) =>
  value ? `${prefix ?? ""}${value}${suffix ?? ""}` : null;

export const AffixInput = ({
  value,
  onChange,
  prefix,
  suffix,
  disabled,
  invalid,
}: {
  value: string;
  onChange: (value: string) => void;
  prefix?: string;
  suffix?: string;
  disabled?: boolean;
  invalid?: boolean;
}) => (
  <div className="relative">
    {prefix && (
      <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-xs text-slate-400">
        {prefix}
      </span>
    )}
    <input
      value={value}
      inputMode="decimal"
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      placeholder="—"
      aria-invalid={invalid}
      className={cn(
        inputClass,
        prefix && "pl-5",
        suffix && "pr-9",
        invalid && "border-red-300 focus:border-red-500 focus:ring-red-500/20"
      )}
    />
    {suffix && (
      <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-slate-400">
        {suffix}
      </span>
    )}
  </div>
);

export default function ValueAddedServices() {
  const dispatch = useDispatch();
  const valueAddedServiceList = useSelector(
    (state: AppState) => state.master.valueAddedService.valueAddedServiceList
  );
  const isPutApiSuccess = useSelector((state: AppState) => state.apiStatus.isPutSuccess);

  const [categories, setCategories] = useState<IVasCategory[]>(() => cloneCategories());
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  // Copy of the charges taken when editing starts; null means view mode
  const [snapshot, setSnapshot] = useState<IVasCategory[] | null>(null);
  const isEditing = snapshot !== null;

  useEffect(() => {
    dispatch(getValueAddedServiceList());
  }, [dispatch]);

  useEffect(() => {
    setCategories(mergeApiValues(valueAddedServiceList));
  }, [valueAddedServiceList]);

  useEffect(() => {
    if (isPutApiSuccess) {
      dispatch(setPutApiStatus(false));
      setSnapshot(null);
    }
  }, [isPutApiSuccess, dispatch]);

  const startEditing = () => setSnapshot(cloneCategories(categories));

  const cancelEditing = () => {
    if (snapshot) setCategories(snapshot);
    setSnapshot(null);
  };

  const saveChanges = () => {
    const charges = categories.flatMap((category) => category.charges);
    if (charges.some((item) => isInvalidNumber(item.value) || isInvalidNumber(item.minimum))) {
      toast.error("Value and Minimum must be numbers", { position: "top-right" });
      return;
    }

    // Send only the rows whose value or minimum differs from when editing started
    const original = new Map((snapshot ?? []).flatMap((category) => category.charges).map((item) => [item.code, item]));
    const changed = charges
      .filter((item): item is IVasCharge & { dbId: number } => {
        const before = original.get(item.code);
        return (
          item.dbId !== null &&
          (!before ||
            toPayloadValue(before.value) !== toPayloadValue(item.value) ||
            toPayloadValue(before.minimum) !== toPayloadValue(item.minimum))
        );
      })
      .map((item) => ({
        id: item.dbId,
        value: toPayloadValue(item.value),
        minimum: toPayloadValue(item.minimum),
      }));

    if (changed.length === 0) {
      toast.info("No changes to save", { position: "top-right" });
      setSnapshot(null);
      return;
    }

    const payload: IValueAddedServiceUpdatePayload = { charges: changed };
    dispatch(editValueAddedServices(payload));
  };

  const updateCharge = (categoryKey: string, chargeId: number, patch: Partial<IVasCharge>) =>
    setCategories((prev) =>
      prev.map((category) =>
        category.key !== categoryKey
          ? category
          : {
              ...category,
              charges: category.charges.map((item) => (item.id === chargeId ? { ...item, ...patch } : item)),
            }
      )
    );

  const filteredCategories = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return categories;
    return categories
      .map((category) => ({
        ...category,
        charges: category.charges.filter(
          (item) => item.name.toLowerCase().includes(term) || item.description.toLowerCase().includes(term)
        ),
      }))
      .filter((category) => category.charges.length > 0);
  }, [categories, search]);

  const allCharges = categories.flatMap((category) => category.charges);

  return (
    <main className="min-h-screen bg-slate-50/70">
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-4 px-3 py-6 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ label: "Accounts", to: "/" }, { label: "Value Added Services" }]} />

        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <CardHeader
            icon={<Layers size={16} />}
            title="Value Added Services"
            subtitle={`${allCharges.length} charges`}
            right={
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <button type="button" className={ghostBtn} onClick={cancelEditing}>
                      <X size={14} />
                      Cancel
                    </button>
                    <button type="button" className={primaryBtn} onClick={saveChanges}>
                      <Save size={14} />
                      Save Changes
                    </button>
                  </>
                ) : (
                  <button type="button" className={primaryBtn} onClick={startEditing}>
                    <Edit2 size={14} />
                    Edit
                  </button>
                )}
              </div>
            }
          />

          <div className="flex flex-wrap items-center gap-3 px-5 py-3">
            <div className="relative w-full max-w-xs">
              <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search charge name or description"
                className={cn(inputClass, "h-9 pl-8")}
              />
            </div>
          </div>
        </section>

        {filteredCategories.map((category) => {
          const isCollapsed = collapsed[category.key];

          return (
            <section key={category.key} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3">
                <button
                  type="button"
                  onClick={() => setCollapsed((prev) => ({ ...prev, [category.key]: !prev[category.key] }))}
                  className="flex min-w-0 items-center gap-2 text-left"
                >
                  <ChevronDown
                    size={16}
                    className={cn("shrink-0 text-slate-400 transition-transform", isCollapsed && "-rotate-90")}
                  />
                  <h2 className="truncate text-sm font-semibold text-slate-900">{category.title}</h2>
                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">
                    {category.charges.length}
                  </span>
                </button>
              </div>

              {!isCollapsed && (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1100px] border-separate border-spacing-0 text-left">
                    <thead>
                      <tr>
                        <th className={cn(thClass, "w-14")}>#</th>
                        <th className={cn(thClass, "w-[220px]")}>Charge Name</th>
                        <th className={thClass}>Description</th>
                        <th className={cn(thClass, "w-[180px]")}>Applied On</th>
                        <th className={cn(thClass, "w-[190px]")}>Calculation Type</th>
                        <th className={cn(thClass, "w-[130px]")}>Value</th>
                        <th className={cn(thClass, "w-[120px]")}>Minimum (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {category.charges.map((item) => {
                        const affix = getValueAffix(item.calculationType);
                        const update = (patch: Partial<IVasCharge>) => updateCharge(category.key, item.id, patch);

                        return (
                          <tr key={item.id} className="transition hover:bg-slate-50/60">
                            <td className={cn(tdClass, "text-slate-400")}>{item.id}</td>
                            <td className={cn(tdClass, "font-medium text-slate-800")}>{item.name}</td>
                            <td className={cn(tdClass, "whitespace-normal text-slate-500")}>{item.description}</td>
                            <td className={tdClass}>{item.appliedOn || <Dash />}</td>
                            <td className={tdClass}>
                              {item.calculationType ? (
                                <span className="inline-flex w-fit items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
                                  {item.calculationType}
                                </span>
                              ) : (
                                <Dash />
                              )}
                            </td>
                            {isEditing ? (
                              <>
                                <td className={tdClass}>
                                  <AffixInput
                                    value={item.value}
                                    onChange={(value) => update({ value })}
                                    prefix={affix.prefix}
                                    suffix={affix.suffix}
                                    invalid={isInvalidNumber(item.value)}
                                  />
                                </td>
                                <td className={tdClass}>
                                  <AffixInput
                                    value={item.minimum}
                                    onChange={(minimum) => update({ minimum })}
                                    prefix="₹"
                                    invalid={isInvalidNumber(item.minimum)}
                                  />
                                </td>
                              </>
                            ) : (
                              <>
                                <td className={cn(tdClass, "font-medium text-slate-800")}>
                                  {formatValue(item.value, affix.prefix, affix.suffix) ?? <Dash />}
                                </td>
                                <td className={tdClass}>{formatValue(item.minimum, "₹") ?? <Dash />}</td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}

        {filteredCategories.length === 0 && (
          <section className="rounded-xl border border-slate-200 bg-white px-5 py-12 text-center text-sm text-slate-500 shadow-sm">
            No charges match "{search}"
          </section>
        )}
      </div>
    </main>
  );
}
